import {Texture} from 'pixi.js';
import {
    CommandLineInterface,
    consoleLogger,
    CoreDI,
    DataStorage,
    IEventManagerWithAsync,
    lazyInject,
    ResourceLoader
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {CheatAction, ICheatPluginController, ICheatsModule} from '../../CheatModuleInterfaces';
import {ReelsSetMatrix} from './ReelsSetMatrix';
import {objectHashMapToMap} from '../../helpers';
import {CheatReelsSetMatrixEvents} from './CheatReelsSetMatrixEvents';

type CheatReelsSetMatrixControllerData = {
    loading: boolean;
};

export class CheatReelsSetMatrixController implements ICheatPluginController {
    @lazyInject(DI.Logic)
    protected readonly logic!: ILogic;

    @lazyInject(DI.ResourceManager)
    protected readonly resourceManager!: ResourceManager;

    @lazyInject(CoreDI.ResourceLoader)
    protected readonly resourceLoader!: ResourceLoader;

    @lazyInject(CoreDI.GameConfig)
    protected readonly gameConfig!: GameConfig;

    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected readonly _eventManager!: IEventManagerWithAsync;

    @lazyInject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    protected _module!: ICheatsModule;

    public reelsSetMatrix!: ReelsSetMatrix;
    public symbolsTextures: { [id: number]: any; } = {};
    public store!: DataStorage<CheatReelsSetMatrixControllerData>;

    public init(module: ICheatsModule): void {
        this._module = module;

        this.store = new DataStorage<CheatReelsSetMatrixControllerData>({
            loading: true
        });

        const reelsStrips = this.logic.getCurrentReelsStrips();
        const reelsMatrixSize = this.logic.getBaseGameReelsMatrixSize();

        this.reelsSetMatrix = new ReelsSetMatrix({reelsMatrixSize, reelsStrips});
        this.resourceManager.loadStaticImages(['question_icon.png'], this.onLoadedResources.bind(this));

        this._cli.addCommand('sendCombo', this.setPositions.bind(this));
        this._cli.addCommand('resetCombo', this.resetPositions.bind(this));
        this._cli.addCommand('lastPosition', this.setFinalSpinStopPositions.bind(this));

        this.setupEvents();
    }

    protected setupEvents(): void {
        this._eventManager.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse.bind(this), this);
        this._eventManager.on(LogicEvents.FREE_SPIN_RESPONSE, this.onSpinResponse.bind(this), this);
        this._eventManager.on(LogicEvents.RESPIN_RESPONSE, this.onSpinResponse.bind(this), this);
        this._eventManager.on(LogicEvents.MINI_SPIN_RESPONSE, this.onSpinResponse.bind(this), this);
        this._eventManager.on(LogicEvents.SELECT_RESPONSE, this.onSpinResponse.bind(this), this);

        this._eventManager.on('CheatModule.addStopPositions', this._addStopPositions.bind(this), this);
        this._eventManager.on('CheatModule.clearStopPositions', this._clearStopPositions.bind(this), this);
    }

    protected setFinalSpinStopPositions(): void {
        const stopPositions: number[] = this.logic.getFinalSpinStopPositions();

        this.setPositions(...stopPositions);
    }

    protected resetPositions(): void {
        this.logic.setFilterForStopPositions(null);
        this.removeFilterStopPositions();
    }

    protected onLoadedResources(): void {
        const symbols = objectHashMapToMap<number, string>(this.gameConfig.reelsConfig.symbolsSprites);

        if (symbols.size > 0) {
            this.symbolsTextures[-1] = Texture.from('question_icon.png');

            for (const [id, name] of symbols.entries()) {
                this.symbolsTextures[id] = this.resourceLoader.getTexture(name);
            }
        }

        this.store.set('loading', false);
    }

    public nextSymbolInMatrix(reelIndex: number, rowIndex: number): void {
        this.reelsSetMatrix.nextSymbolInMatrix(reelIndex, rowIndex);
    }

    public previousSymbolInMatrix(reelIndex: number, rowIndex: number): void {
        this.reelsSetMatrix.previousSymbolInMatrix(reelIndex, rowIndex);
    }

    public applyStopFilterPositions(stopPositions: number[]): void {
        this.removePreviousAction();

        this._module.addCheatAction({
            id: `stopPositions:${stopPositions.toString()}`,
            apply: () => {
                consoleLogger.log(`Apply stopPositions - ${stopPositions.toString()}`);

                this.logic.setFilterForStopPositions(stopPositions);
            },
            reject: () => this.logic.setFilterForStopPositions(null)
        });
    }

    public removeFilterStopPositions(): void {
        this.removePreviousAction();
        this.reset();
    }

    public reset(): void {
        const stopPositions = this.reelsSetMatrix.stopPositions.map(() => -1);

        this.reelsSetMatrix.setStopPositions(stopPositions);
    }

    public get stopPositions(): number[] {
        return this.reelsSetMatrix.stopPositions;
    }

    public get matrix(): number[][] {
        return this.reelsSetMatrix.matrix;
    }

    public resetCell(row: number, reel: number): void {
        this.reelsSetMatrix.resetSymbolInMatrix(reel, row);
    }

    protected removePreviousAction(): void {
        const action = this.findAction();

        if (action) {
            this._module.removeCheatAction(action);
        }
    }

    protected findAction(): CheatAction | undefined {
        const {cheatActions} = this._module.storage.data;

        return cheatActions.find(action => action.id.includes('stopPositions:'));
    }

    protected setPositions(...positions: any): void {
        positions = Array.from(positions) as number[];

        this.applyStopFilterPositions(positions);
    }

    protected onSpinResponse(...args: any[]): void {
        if (this.logic.previousAction !== this.logic.nextAction) {
            const reelsStrips = this.logic.getCurrentReelsStrips();
            const reelsMatrixSize = this.logic.getBaseGameReelsMatrixSize();

            this.reelsSetMatrix = new ReelsSetMatrix({reelsMatrixSize, reelsStrips});
            this.reset();
            this._eventManager.emit(CheatReelsSetMatrixEvents.FORCE_UPDATE);
        }
    }

    private _addStopPositions(positions: number[]): void {
        this.setPositions(...positions);
    }

    private _clearStopPositions(): void {
        this.removeFilterStopPositions();
    }
}
